'use client';

import { useEffect } from 'react';

export function AttributeCleaner() {
    useEffect(() => {
        // Function to remove unwanted attributes
        const cleanAttributes = () => {
            const body = document.body;
            const html = document.documentElement;

            // List of unwanted attributes that might be added by extensions or scripts
            const unwantedAttrs = [
                'data-gptw',
                'data-extension',
                'data-browser-extension',
                'data-script-injected'
            ];

            unwantedAttrs.forEach(attr => {
                if (body.hasAttribute(attr)) {
                    body.removeAttribute(attr);
                }
                if (html.hasAttribute(attr)) {
                    html.removeAttribute(attr);
                }
            });
        };

        // Clean immediately
        cleanAttributes();

        // Set up a mutation observer to watch for attribute changes
        const observer = new MutationObserver((mutations) => {
            let shouldClean = false;

            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    const target = mutation.target as Element;
                    if (target === document.body || target === document.documentElement) {
                        const attrName = mutation.attributeName;
                        if (attrName && attrName.startsWith('data-')) {
                            shouldClean = true;
                        }
                    }
                }
            });

            if (shouldClean) {
                // Use a small delay to ensure the attribute is fully added before removing
                setTimeout(cleanAttributes, 10);
            }
        });

        // Observe changes to body and html attributes
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['data-*']
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-*']
        });

        // Clean attributes periodically as a fallback
        const interval = setInterval(cleanAttributes, 1000);

        // Clean on focus and visibility change (common times when extensions run)
        const handleFocus = () => {
            setTimeout(cleanAttributes, 100);
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                setTimeout(cleanAttributes, 100);
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            observer.disconnect();
            clearInterval(interval);
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    return null; // This component doesn't render anything
}
